import React, { useEffect } from "react";
import Layout from "./layout";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { doFetch, clearSearchResults } from "../redux/api/actions";
import { SEARCH_TOURS } from "../redux/api/constants";
import { useTranslation } from "react-i18next";

const TourCard = ({ tour }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const imageUrls = tour.image_urls ? tour.image_urls.split(",").filter(url => url.trim()) : [];

    const formatDuration = (seconds) => {
        if (!seconds) return null;
        const mins = Math.round(seconds / 60);
        if (mins < 60) return `${mins} min`;
        const hours = Math.floor(mins / 60);
        const rem = mins % 60;
        return rem ? `${hours} ${t('hours-label')} ${rem} min` : `${hours} ${t('hours-label')}`;
    };
    
    const handleClick = () => {
        navigate(`/tours/${tour.id}`);
    };

    return (
        <div className="search-tour-card" onClick={handleClick}>
            <div className="tour-card-image">
                {imageUrls.length > 0 ? (
                    <img src={imageUrls[0]} alt={tour.title} />
                ) : (
                    <div className="no-image">
                        <i className="bi bi-image"></i>
                    </div>
                )}
            </div>
            <div className="tour-card-content">
                <div className="tour-card-header">
                    <h3>{tour.title}</h3>
                </div>
                
                <p className="tour-card-description">{tour.description}</p>
                
                <div className="tour-card-meta">
                    {formatDuration(tour.total_duration_seconds) && (
                        <span><i className="bi bi-clock"></i> {formatDuration(tour.total_duration_seconds)}</span>
                    )}
                    <span><i className="bi bi-geo-alt"></i> {t('stops-count', { count: tour.track_count || 0 })}</span>
                    {tour.category && (
                        <span><i className="bi bi-tag"></i> {tour.category}</span>
                    )}
                </div>

                {tour.tourguide && (
                    <div className="tour-card-host">
                        {tour.tourguide.avatar_url && (
                            <img src={tour.tourguide.avatar_url} alt={tour.tourguide.name} className="host-avatar-small" />
                        )}
                        <span>{t('by', { name: tour.tourguide.name })}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const Location = () => {
    const { t } = useTranslation();
    const { location } = useParams();
    
    const searchResults = useSelector(state => state.API.searchResults);
    const loading = useSelector(state => state.API.loading);
    const dispatch = useDispatch();

    // Parse location from URL
    const parseLocation = (location) => {
        const locationArray = location.split("--");
        const city = locationArray[0].replace(/-/g, " ");
        let state = "";
        let country = "";
        
        if (locationArray.length === 2) {
            country = locationArray[1].replace(/-/g, " ");
        } else if (locationArray.length === 3) {
            state = locationArray[1] ? locationArray[1].replace(/-/g, " ") : "";
            country = locationArray[2].replace(/-/g, " ");
        }
        
        return { city, state, country };
    };

    const { city, state, country } = parseLocation(location);
    
    // No date/guest filters for audio tours

    useEffect(() => {
        // Build search query
        let query = `locations/search?city=${encodeURIComponent(city)}`;
        if (state) query += `&state=${encodeURIComponent(state)}`;
        if (country) query += `&country=${encodeURIComponent(country)}`;
        query += `&guests=1`;

        dispatch(doFetch(SEARCH_TOURS, query, null));

        return () => {
            dispatch(clearSearchResults());
        };
    }, [dispatch, city, state, country]);

    const locationDisplay = searchResults?.location 
        ? `${searchResults.location.city}${searchResults.location.state ? `, ${searchResults.location.state}` : ''}`
        : `${city}${state ? `, ${state}` : ''}`;

    const countryFlag = searchResults?.location?.flag || '';

    return (
        <Layout selected="location">
            <div className="search-results-page">
                <div className="search-results-header">
                    <nav aria-label="breadcrumb" className="breadcrumb-nav">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/">{t('home')}</Link>
                            </li>
                            <li className="breadcrumb-item active">{locationDisplay}</li>
                        </ol>
                    </nav>
                    
                    <div className="location-hero">
                        <h1>
                            {t('location-experiences-in', { location: `${locationDisplay} ${countryFlag}`.trim() })}
                        </h1>
                        <p className="results-count">
                            {t('location-tours-available', { count: searchResults?.tours?.length || 0 })}
                        </p>
                    </div>
                    <div className="search-filters">
                        <div className="text-muted small">
                            {t('location-audio-tours-note')}
                        </div>
                    </div>
                </div>

                <div className="search-results-content">
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">{t('loading')}</span>
                            </div>
                        </div>
                    ) : searchResults?.tours && searchResults.tours.length > 0 ? (
                        <div className="tours-grid-search">
                            {searchResults.tours.map((tour) => (
                                <TourCard key={tour.id} tour={tour} />
                            ))}
                        </div>
                    ) : (
                        <div className="empty-results">
                            <i className="bi bi-compass"></i>
                            <h3>{t('location-no-tours-found')}</h3>
                            <p>
                                {t('location-empty-description')}
                            </p>
                            <Link to="/tourguide/about" className="btn-primary mt-3">
                                {t('location-become-guide-here')}
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default Location;
